function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, KeyboardSensor, useSensor, useSensors, MeasuringStrategy, DragOverlay, defaultDropAnimation } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getProjection } from "./utilities.js";
import { sortableTreeKeyboardCoordinates } from "./keyboardCoordinates.js";
import PointerSensorWithInteraction from "./PointerSensorWithInteraction.js";
import { TreeItem } from "./TreeItem.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var MEASURING = {
  droppable: {
    strategy: MeasuringStrategy.Always
  }
};
var CONSTRAINT = {
  activationConstraint: {
    distance: 5
  }
};

// Disabling pointer events allows us to use scroll wheel while dragging
var DRAG_OVERLAY_STYLE = {
  pointerEvents: 'none'
};
var dropAnimationConfig = {
  keyframes(_ref) {
    var transform = _ref.transform;
    return [{
      opacity: 1,
      transform: CSS.Transform.toString(transform.initial)
    }, {
      opacity: 0,
      transform: CSS.Transform.toString(transform.final)
    }];
  },
  easing: 'ease-out',
  sideEffects(_ref2) {
    var active = _ref2.active;
    active.node.animate([{
      opacity: 0
    }, {
      opacity: 1
    }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing
    });
  }
};

/**
 * This adjusts the transform to move to the cursor if it gets shifted due to multi-select.
 * With multi-select, the selected items (except dragged) are removed on drag.
 * This can cause the overlay item to disconnect from the cursor in some cases.
 * E.g. select first 3 items, start dragging from 3rd item.
 * Without this modifier, the drag overlay will be shifted 60px up from the cursor after the items are removed.
 *
 * This assumes all items are the same height as the dragged item
 * @param args Modifier args from dnd-kit
 * @returns Transform so that the dragged item stays on the cursor
 */
function adjustToCursor(args) {
  var _adjustToCursor$offse;
  if (adjustToCursor.offsetY == null && args.activeNodeRect && args.activatorEvent instanceof PointerEvent) {
    adjustToCursor.offsetY = Math.floor((args.activatorEvent.clientY - args.activeNodeRect.top) / args.activeNodeRect.height) * args.activeNodeRect.height;
  }
  if (!args.activeNodeRect) {
    adjustToCursor.offsetY = null;
  }
  return _objectSpread(_objectSpread({}, args.transform), {}, {
    y: args.transform.y + ((_adjustToCursor$offse = adjustToCursor.offsetY) !== null && _adjustToCursor$offse !== void 0 ? _adjustToCursor$offse : 0)
  });
}

// Used to track the offset for adjustToCursor
// Once drag starts, set this. Once it ends, null this
// Kind of hacky to store it as a property on the function,
// but avoids a singleton state or needing hooks to maintain this.
// The logic came from the dnd-kit example.
adjustToCursor.offsetY = null;

// From https://github.com/clauderic/dnd-kit/pull/334
var fixCursorSnapOffset = args => {
  // Bail out if keyboard activated
  if (!args.pointerCoordinates) {
    return closestCenter(args);
  }
  var _args$pointerCoordina = args.pointerCoordinates,
    x = _args$pointerCoordina.x,
    y = _args$pointerCoordina.y;
  var _args$collisionRect = args.collisionRect,
    width = _args$collisionRect.width,
    height = _args$collisionRect.height;
  var updated = _objectSpread(_objectSpread({}, args), {}, {
    // The collision rectangle is broken when using adjustToCursor. Reset
    // the collision rectangle based on pointer location and overlay size.
    collisionRect: {
      width,
      height,
      bottom: y + height / 2,
      left: x - width / 2,
      right: x + width / 2,
      top: y - height / 2
    }
  });
  return closestCenter(updated);
};
export default function SortableTreeDndContext(_ref3) {
  var items = _ref3.items,
    _ref3$indentationWidt = _ref3.indentationWidth,
    indentationWidth = _ref3$indentationWidt === void 0 ? 30 : _ref3$indentationWidt,
    onDragStart = _ref3.onDragStart,
    onDragEnd = _ref3.onDragEnd,
    children = _ref3.children,
    renderItem = _ref3.renderItem;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    activeId = _useState2[0],
    setActiveId = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    offsetLeft = _useState4[0],
    setOffsetLeft = _useState4[1];
  var activeItem = activeId != null ? items.find(_ref4 => {
    var id = _ref4.id;
    return id === activeId;
  }) : null;
  var sensorContext = useRef({
    items,
    offset: offsetLeft
  });
  var keyboardOptions = useMemo(() => ({
    coordinateGetter: sortableTreeKeyboardCoordinates(sensorContext, indentationWidth),
    keyboardCodes: {
      // Default is space and enter for start/end,
      // but enter is used to select items from the search list
      start: ['Space'],
      cancel: ['Escape'],
      end: ['Space']
    }
  }), [indentationWidth]);
  var sensors = useSensors(useSensor(PointerSensorWithInteraction, CONSTRAINT), useSensor(KeyboardSensor, keyboardOptions));
  var sortedIds = useMemo(() => items.map(_ref5 => {
    var id = _ref5.id;
    return id;
  }), [items]);
  useEffect(() => {
    sensorContext.current = {
      items,
      offset: offsetLeft
    };
  }, [items, offsetLeft]);
  var handleDragStart = useCallback(event => {
    var newActiveId = event.active.id;
    setActiveId(newActiveId);
    onDragStart === null || onDragStart === void 0 || onDragStart(newActiveId, event);
    document.body.style.setProperty('cursor', 'grabbing');
  }, [onDragStart]);
  var handleDragMove = useCallback(_ref6 => {
    var delta = _ref6.delta;
    setOffsetLeft(delta.x);
  }, []);
  var resetState = useCallback(() => {
    setActiveId(null);
    setOffsetLeft(0);
    document.body.style.setProperty('cursor', '');
  }, []);
  var handleDragEnd = useCallback(_ref7 => {
    var _active$rect$current$, _active$rect$current$2, _active$rect$current$3, _active$rect$current$4;
    var active = _ref7.active,
      over = _ref7.over;
    var projected = active.id != null && (over === null || over === void 0 ? void 0 : over.id) != null ? getProjection(items, active.id, over.id, ((_active$rect$current$ = (_active$rect$current$2 = active.rect.current.translated) === null || _active$rect$current$2 === void 0 ? void 0 : _active$rect$current$2.left) !== null && _active$rect$current$ !== void 0 ? _active$rect$current$ : 0) - ((_active$rect$current$3 = (_active$rect$current$4 = active.rect.current.initial) === null || _active$rect$current$4 === void 0 ? void 0 : _active$rect$current$4.left) !== null && _active$rect$current$3 !== void 0 ? _active$rect$current$3 : 0), indentationWidth) : null;
    if (projected && over) {
      var depth = projected.depth,
        parentId = projected.parentId;
      var clonedItems = JSON.parse(JSON.stringify(items));
      var overIndex = clonedItems.findIndex(_ref8 => {
        var id = _ref8.id;
        return id === over.id;
      });
      var activeIndex = clonedItems.findIndex(_ref9 => {
        var id = _ref9.id;
        return id === active.id;
      });
      var activeTreeItem = clonedItems[activeIndex];
      clonedItems[activeIndex] = _objectSpread(_objectSpread({}, activeTreeItem), {}, {
        depth,
        parentId
      });
      onDragEnd === null || onDragEnd === void 0 || onDragEnd(activeTreeItem, _objectSpread(_objectSpread({}, clonedItems[overIndex]), {}, {
        parentId: projected.parentId
      }));
    }
    resetState();
  }, [items, indentationWidth, onDragEnd, resetState]);
  var handleDragCancel = useCallback(() => {
    resetState();
  }, [resetState]);
  return /*#__PURE__*/_jsx(DndContext, {
    sensors: sensors,
    collisionDetection: fixCursorSnapOffset,
    measuring: MEASURING,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
    children: /*#__PURE__*/_jsxs(SortableContext, {
      items: sortedIds,
      strategy: verticalListSortingStrategy,
      children: [children, /*#__PURE__*/createPortal(/*#__PURE__*/_jsx(DragOverlay, {
        dropAnimation: dropAnimationConfig,
        modifiers: [adjustToCursor],
        className: "visibility-ordering-list",
        style: DRAG_OVERLAY_STYLE,
        children: activeId != null && activeItem ? /*#__PURE__*/_jsx(TreeItem, {
          depth: activeItem.depth,
          clone: true,
          value: activeId,
          renderItem: renderItem,
          item: activeItem
        }) : null
      }), document.body)]
    })
  });
}
//# sourceMappingURL=SortableTreeDndContext.js.map